import React, { useState } from 'react';
import { ArrowLeft, Search, Filter, CheckCircle2, Clock3, XCircle, FileText, Calendar, DollarSign, Package, Send, PlayCircle, RefreshCcw } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Quote, QuoteStatus, Material, QuoteProductItem, UserProfile } from '../types';
import { formatMinutes, translateQuoteStatus } from '../utils/formatUtils';
const PDFExporter = React.lazy(() => import('./PDFExporter'));
import { CATEGORIES } from '../constants/categories';
import { TUTORIAL_LINKS } from '../constants';

interface Props {
  quotes: Quote[];
  /** Catálogo de materiais atual do usuário — usado para detectar drift de custo. */
  materials: Material[];
  user: UserProfile;
  onBack: () => void;
  onUpdateQuoteStatus: (id: string, status: Quote['status']) => void;
  onReuseQuote: (quote: Quote) => void;
}

export default function QuoteHistory({ quotes, materials, user, onBack, onUpdateQuoteStatus, onReuseQuote }: Props) {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<QuoteStatus | 'Todos'>('Todos');
  const [selectedQuote, setSelectedQuote] = useState<Quote | null>(null);
  const [toastMessage, setToastMessage] = useState<{title: string, type: 'success' | 'error' | 'info'} | null>(null);

  const showToast = (title: string, type: 'success' | 'error' | 'info') => {
    setToastMessage({ title, type });
    setTimeout(() => setToastMessage(null), 3000);
  };

  const filteredQuotes = quotes
    .filter(q => filterStatus === 'Todos' || q.status === filterStatus)
    .filter(q => 
      (q.clientName || '').toLowerCase().includes(searchTerm.toLowerCase()) || 
      (q.rawServiceName || '').toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

  const parentRef = React.useRef<HTMLDivElement>(null);

  // Virtualizador com medição dinâmica de altura dos cards
  const rowVirtualizer = useVirtualizer({
    count: filteredQuotes.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 140,
    overscan: 5,
  });

  // Bugfix de Filtragem: Reseta o scroll para o topo ao alterar busca/filtros
  React.useEffect(() => {
    parentRef.current?.scrollTo(0, 0);
  }, [searchTerm, filterStatus]);

  const formatCurrency = (value: number) => {
    const formatted = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
    return formatted.replace(/^R\$\s?R\$/, 'R$');
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusIcon = (status: QuoteStatus) => {
    switch (status) {
      case 'approved': return <CheckCircle2 className="h-4 w-4 text-emerald-600" />;
      case 'sent': return <Clock3 className="h-4 w-4 text-amber-600" />;
      case 'rejected': return <XCircle className="h-4 w-4 text-red-600" />;
      case 'draft': return <FileText className="h-4 w-4 text-zinc-400" />;
      default: return null;
    }
  };

  const getStatusColor = (status: QuoteStatus) => {
    switch (status) {
      case 'approved': return 'bg-emerald-50 text-emerald-700 border-emerald-200';
      case 'sent': return 'bg-amber-50 text-amber-700 border-amber-200';
      case 'rejected': return 'bg-red-50 text-red-700 border-red-200';
      case 'draft': return 'bg-zinc-100 text-zinc-600 border-zinc-200';
      default: return 'bg-zinc-100 text-zinc-600 border-zinc-200';
    }
  };

  const calculateDaysSince = (dateString: string) => {
    const created = new Date(dateString);
    const today = new Date();
    const diffTime = Math.abs(today.getTime() - created.getTime());
    return Math.floor(diffTime / (1000 * 60 * 60 * 24));
  };

  /**
   * Compara o snapshotPrice de cada produto com o preço atual do material
   * no catálogo. Retorna apenas os itens com drift >= 5%.
   */
  const computeDriftAlerts = (quote: Quote): Array<{ name: string; snapshotPrice: number; currentPrice: number; driftPct: number }> => {
    if (!quote.products?.length || !materials.length) return [];
    const alerts: Array<{ name: string; snapshotPrice: number; currentPrice: number; driftPct: number }> = [];

    quote.products.forEach((p: QuoteProductItem) => {
      if (typeof p.snapshotPrice !== 'number') return;

      // Busca por materialId explícito ou fallback por nome
      const current = p.materialId
        ? materials.find(m => m.id === p.materialId)
        : materials.find(m => {
            const mn = (m.name || '').toLowerCase();
            const pn = (p.name || '').toLowerCase();
            return mn.includes(pn) || pn.includes(mn);
          });

      if (!current) return;

      const driftPct = ((current.pricePaid - p.snapshotPrice) / p.snapshotPrice) * 100;
      if (Math.abs(driftPct) >= 5) {
        alerts.push({
          name: p.name,
          snapshotPrice: p.snapshotPrice,
          currentPrice: current.pricePaid,
          driftPct,
        });
      }
    });

    return alerts;
  };

  const handleFollowUp = async (quote: Quote) => {
    const daysSince = calculateDaysSince(quote.createdAt);
    const firstName = (quote.clientName || 'Cliente').split(' ')[0];
    const message = `Olá ${firstName}, tudo bem? Passando para saber se conseguiu avaliar o orçamento de ${quote.rawServiceName} que enviamos há ${daysSince} dias. Qualquer dúvida estou à disposição!`;
    const encodedMessage = encodeURIComponent(message);
    
    // Tenta usar a Web Share API (melhor para Mobile nativo)
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Cobrar Retorno do Orçamento',
          text: message
        });
        return;
      } catch (e) {
        // Se falhar ou for cancelado, segue para o fallback de link direto
        console.warn('Web Share API falhou, usando fallback wa.me:', e);
      }
    }

    // Fallback padrão wa.me (Desktop ou Mobile sem share API)
    window.open(`https://wa.me/?text=${encodedMessage}`, '_blank');
  };

  return (
    <div className="h-dvh flex flex-col overflow-hidden bg-zinc-50 font-sans">
      {/* Header */}
      <header className="bg-white border-b border-zinc-200 px-4 sm:px-6 py-4 flex items-center gap-4 sticky top-0 z-20 shrink-0">
        <button onClick={onBack} className="p-2 hover:bg-zinc-100 rounded-full transition-colors">
          <ArrowLeft className="h-5 w-5 text-zinc-600" />
        </button>
        <div className="flex-1">
          <h1 className="text-lg font-bold text-zinc-900 leading-none">Histórico de Orçamentos</h1>
          <p className="text-[10px] text-zinc-400 font-bold uppercase tracking-wider mt-1">Todos os seus registros</p>
        </div>
        {TUTORIAL_LINKS.historico ? (
          <a 
            href={TUTORIAL_LINKS.historico}
            target="_blank"
            rel="noopener noreferrer"
            title="Assistir Vídeo Tutorial desta tela"
            className="p-2 ml-2 bg-blue-50 border border-blue-200 text-blue-600 rounded-xl flex items-center gap-2 hover:bg-blue-100 transition-colors shadow-sm shrink-0 cursor-pointer"
          >
            <PlayCircle className="h-5 w-5" />
            <span className="text-[10px] font-bold uppercase tracking-widest hidden sm:inline">Como Usar</span>
          </a>
        ) : (
          <button 
            title="Em breve: Vídeo Tutorial desta tela"
            className="p-2 ml-2 bg-zinc-100/50 border border-zinc-200/50 rounded-xl flex items-center gap-2 text-zinc-400 opacity-60 cursor-not-allowed shrink-0"
          >
            <PlayCircle className="h-5 w-5" />
            <span className="text-[10px] font-bold uppercase tracking-widest hidden sm:inline">Como Usar</span>
          </button>
        )}
      </header>

      <main className="flex-1 flex flex-col min-h-0 p-4 max-w-3xl mx-auto w-full space-y-4">
        {/* Search and Filter */}
        <div className="bg-white p-4 rounded-2xl shadow-sm border border-zinc-100 space-y-4 shrink-0">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-zinc-400" />
            <input
              type="text"
              placeholder="Buscar por cliente ou serviço..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-zinc-50 border border-zinc-200 rounded-xl text-sm focus:ring-2 focus:ring-brand outline-none transition-all"
            />
          </div>
          
          <div className="flex w-full gap-1">
            {['Todos', 'draft', 'sent', 'approved', 'rejected'].map((status) => (
              <button
                key={status}
                onClick={() => setFilterStatus(status as any)}
                className={`flex-1 px-1 py-2 rounded-xl text-[10px] sm:text-xs font-bold transition-colors ${
                  filterStatus === status 
                    ? 'bg-zinc-900 text-white' 
                    : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'
                }`}
              >
                {status === 'Todos' ? 'Todos' : translateQuoteStatus(status)}
              </button>
            ))}
          </div>
        </div>

        {/* Container de Rolagem e Listagem Virtualizada */}
        <div className="flex-1 min-h-0 flex flex-col">
          {filteredQuotes.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-2xl border border-zinc-100 shadow-sm shrink-0">
              <div className="bg-zinc-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <FileText className="h-8 w-8 text-zinc-400" />
              </div>
              <h3 className="text-zinc-900 font-medium mb-1">Nenhum orçamento encontrado</h3>
              <p className="text-sm text-zinc-500">Tente mudar os filtros ou fazer uma nova busca.</p>
            </div>
          ) : (
            <div 
              ref={parentRef}
              className="flex-1 min-h-0 h-full overflow-y-auto pr-1 pb-20"
            >
              <div
                style={{
                  height: `${rowVirtualizer.getTotalSize()}px`,
                  width: '100%',
                  position: 'relative',
                }}
              >
                {rowVirtualizer.getVirtualItems().map((virtualItem) => {
                  const quote = filteredQuotes[virtualItem.index];
                  return (
                    <div
                      key={virtualItem.key}
                      ref={rowVirtualizer.measureElement}
                      data-index={virtualItem.index}
                      className="absolute left-0 top-0 w-full"
                      style={{
                        transform: `translateY(${virtualItem.start}px)`,
                      }}
                    >
                      <div 
                        onClick={() => setSelectedQuote(quote)}
                        className="bg-white rounded-2xl p-4 shadow-sm border border-zinc-100 cursor-pointer hover:border-brand/20 transition-colors"
                      >
                        <div className="flex justify-between items-start mb-3">
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="w-10 h-10 bg-brand/10 rounded-full flex items-center justify-center text-brand font-bold shrink-0">
                              {quote.clientName?.charAt(0) || 'C'}
                            </div>
                            <div className="min-w-0">
                              <div className="flex items-center gap-1.5">
                                <h3 className="font-bold text-zinc-900 truncate text-sm leading-tight">{quote.clientName || 'Cliente'}</h3>
                                <span className="text-[10px] text-zinc-400 font-mono shrink-0">#{quote.id.slice(-4).toUpperCase()}</span>
                              </div>
                              <p className="text-xs text-zinc-500 line-clamp-1 mt-0.5">{quote.rawServiceName}</p>
                              {quote.serviceDescription && (
                                <p className="text-[10px] text-zinc-400 line-clamp-1 mt-0.5 italic">{quote.serviceDescription}</p>
                              )}
                            </div>
                          </div>
                          <div className={`flex items-center gap-1 px-2 py-1 rounded-lg border text-[10px] font-bold uppercase tracking-wider shrink-0 ${getStatusColor(quote.status)}`}>
                            {getStatusIcon(quote.status)}
                            {translateQuoteStatus(quote.status)}
                          </div>
                        </div>
                        
                        <div className="flex justify-between items-end mt-4 pt-4 border-t border-zinc-50">
                          <div className="flex items-center gap-2 text-[10px] text-zinc-400 font-medium">
                            <Calendar className="h-3 w-3" />
                            {formatDate(quote.createdAt)}
                          </div>
                          
                          <div className="flex items-center gap-3">
                            {quote.status === 'sent' && (
                              <button 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleFollowUp(quote);
                                }}
                                className="w-8 h-8 rounded-full bg-[#25D366] hover:bg-[#128C7E] text-white flex items-center justify-center transition-all active:scale-90 shadow-xs shrink-0"
                                title="Cobrar Retorno do Orçamento"
                              >
                                <svg className="h-4.5 w-4.5 fill-current" viewBox="0 0 24 24">
                                  <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946C.06 5.348 5.397.01 12.008.01c3.202.001 6.212 1.246 8.477 3.513 2.262 2.268 3.507 5.28 3.505 8.484-.004 6.657-5.34 11.997-11.953 11.997-2.005-.001-3.973-.502-5.724-1.455L0 24zm6.09-3.931l.34.202c1.611.956 3.473 1.46 5.378 1.461 5.922 0 10.743-4.82 10.747-10.743.002-2.869-1.111-5.566-3.136-7.592-2.023-2.024-4.717-3.137-7.587-3.138-5.929 0-10.75 4.82-10.754 10.742-.002 1.953.51 3.856 1.48 5.567l.228.4-.969 3.539 3.619-.949zm12.39-7.228c-.34-.17-2.015-.995-2.327-1.109-.311-.115-.538-.17-.763.17-.225.34-.871 1.11-1.069 1.337-.197.226-.394.254-.734.085-.34-.17-1.437-.53-2.738-1.691-1.012-.903-1.694-2.02-1.892-2.36-.197-.339-.021-.523.149-.692.152-.152.34-.397.51-.595.17-.198.226-.339.34-.565.113-.226.056-.424-.028-.593-.085-.17-.763-1.838-1.045-2.518-.275-.66-.554-.57-.763-.58l-.651-.01c-.225 0-.593.085-.904.424-.311.34-1.187 1.161-1.187 2.83 0 1.669 1.215 3.282 1.385 3.51.17.226 2.39 3.65 5.79 5.12 2.85 1.233 3.906 1.144 5.31.934 1.096-.164 2.327-.95 2.659-1.867.33-.917.33-1.705.231-1.874-.099-.17-.367-.269-.707-.44z"/>
                                </svg>
                              </button>
                            )}
                            <div className="text-lg font-black text-brand">
                              {formatCurrency(quote.finalPrice)}
                            </div>
                          </div>
                        </div>
                      </div>
                      {/* Espaçador de gap (12px) cross-browser seguro */}
                      <div className="h-3 w-full" />
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Quote Detail Modal */}
      {selectedQuote && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 animate-in fade-in">
          <div className="bg-white w-full max-w-md sm:max-w-lg rounded-t-4xl sm:rounded-4xl overflow-hidden shadow-2xl animate-in slide-in-from-bottom-8 sm:slide-in-from-bottom-0 sm:zoom-in-95 flex flex-col max-h-[95dvh] sm:max-h-[90vh]">
            <div className="p-6 border-b border-zinc-100 flex justify-between items-center bg-zinc-50">
              <div>
                <h2 className="text-lg font-bold text-zinc-900">Detalhes do Orçamento</h2>
                <p className="text-[10px] text-zinc-500 font-bold uppercase tracking-wider mt-1">
                  {formatDate(selectedQuote.createdAt)}
                </p>
              </div>
              <button 
                onClick={() => setSelectedQuote(null)}
                className="p-2 bg-white rounded-full hover:bg-zinc-100 transition-colors shadow-sm"
              >
                <XCircle className="h-5 w-5 text-zinc-400" />
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-6 space-y-6 custom-scrollbar">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-brand/10 rounded-full flex items-center justify-center text-brand font-bold text-xl">
                    {selectedQuote.clientName?.charAt(0) || 'C'}
                  </div>
                  <div>
                    <h3 className="font-bold text-zinc-900 text-lg">{selectedQuote.clientName || 'Cliente'}</h3>
                    <p className="text-sm text-zinc-500">{selectedQuote.category}</p>
                  </div>
                </div>
                <div className={`flex items-center gap-1 px-3 py-1.5 rounded-xl border text-xs font-bold uppercase tracking-wider ${getStatusColor(selectedQuote.status)}`}>
                  {getStatusIcon(selectedQuote.status)}
                  {translateQuoteStatus(selectedQuote.status)}
                </div>
              </div>

              <div className="bg-zinc-50 rounded-2xl p-4 border border-zinc-100">
                <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest mb-1">Serviço</p>
                <p className="text-zinc-900 font-medium">{selectedQuote.rawServiceName}</p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="bg-white border border-zinc-100 rounded-2xl p-3 shadow-sm">
                  <div className="flex items-center gap-2 text-zinc-500 mb-1">
                    <Package className="h-4 w-4" />
                    <span className="text-[10px] font-bold uppercase tracking-wider">Material</span>
                  </div>
                  <p className="font-mono text-sm font-medium">{formatCurrency(selectedQuote.materialCost)}</p>
                </div>
                <div className="bg-white border border-zinc-100 rounded-2xl p-3 shadow-sm">
                  <div className="flex items-center gap-2 text-zinc-500 mb-1">
                    <Clock3 className="h-4 w-4" />
                    <span className="text-[10px] font-bold uppercase tracking-wider">Tempo Estimado</span>
                  </div>
                  <p className="font-mono text-sm font-medium text-zinc-900">{formatMinutes(selectedQuote.timeSpentMinutes)}</p>
                </div>
              </div>

              <div className="bg-brand-dark text-white rounded-2xl p-5 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-brand/20 rounded-full blur-3xl -mr-16 -mt-16" />
                <div className="relative z-10 flex justify-between items-end">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <p className="text-brand/50 text-[10px] font-bold uppercase tracking-widest">Valor Final</p>
                      <span className="bg-brand/20 text-brand text-[9px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wider">
                        +M. de Lucro: {Math.round(selectedQuote.appliedProfitMargin * 100)}%
                      </span>
                    </div>
                    <p className="text-3xl font-black">{formatCurrency(selectedQuote.finalPrice)}</p>
                  </div>
                  <DollarSign className="h-8 w-8 text-brand/30" />
                </div>
              </div>

              {/* Status History Section */}
              <div className="space-y-3">
                <h4 className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Histórico de Status</h4>
                <div className="space-y-4">
                  {(selectedQuote.statusHistory || []).map((entry, index) => (
                    <div key={index} className="flex gap-3 relative">
                      {index !== (selectedQuote.statusHistory?.length || 0) - 1 && (
                        <div className="absolute left-2.75 top-6 -bottom-4 w-0.5 bg-zinc-100" />
                      )}
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center z-10 ${
                        entry.status === 'approved' ? 'bg-emerald-100' : 
                        entry.status === 'rejected' ? 'bg-red-100' : 
                        entry.status === 'sent' ? 'bg-brand/10' : 'bg-zinc-100'
                      }`}>
                        <div className={`w-2 h-2 rounded-full ${
                          entry.status === 'approved' ? 'bg-emerald-600' : 
                          entry.status === 'rejected' ? 'bg-red-600' : 
                          entry.status === 'sent' ? 'bg-brand' : 'bg-zinc-400'
                        }`} />
                      </div>
                      <div className="flex-1 pb-2">
                        <div className="flex justify-between items-start">
                          <p className="text-sm font-bold text-zinc-900">{translateQuoteStatus(entry.status)}</p>
                          <p className="text-[10px] text-zinc-400">{formatDate(entry.timestamp)}</p>
                        </div>
                        <p className="text-[10px] text-zinc-500 italic">
                          {entry.status === 'draft' ? 'Orçamento em elaboração' : 
                           entry.status === 'sent' ? 'Orçamento enviado ao cliente' : 
                           entry.status === 'approved' ? 'Venda confirmada pelo usuário' : 
                           'Orçamento marcado como perdido'}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {selectedQuote.status === 'sent' && (
                <div className="space-y-3 pt-2">
                  {/* --- ALERTA DE DRIFT DE CUSTO --- */}
                  {(() => {
                    const drifts = computeDriftAlerts(selectedQuote);
                    if (!drifts.length) return null;
                    return (
                      <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 space-y-3">
                        <div className="flex items-start gap-2">
                          <span className="text-amber-500 text-base leading-none mt-0.5">⚠</span>
                          <div>
                            <p className="font-bold text-amber-900 text-sm">Custos alterados desde a criação</p>
                            <p className="text-[11px] text-amber-700 mt-0.5">O preço de {drifts.length === 1 ? 'um insumo mudou' : `${drifts.length} insumos mudaram`} após este orçamento ser salvo. O valor do cliente não é afetado.</p>
                          </div>
                        </div>
                        <div className="space-y-2">
                          {drifts.map((d, idx) => (
                            <div key={idx} className="flex items-center justify-between bg-white rounded-xl p-3 border border-amber-100">
                              <span className="text-xs font-bold text-zinc-700 truncate max-w-[60%]">{d.name}</span>
                              <div className="text-right">
                                <span className={`text-xs font-black ${ d.driftPct > 0 ? 'text-rose-600' : 'text-emerald-600' }`}>
                                  {d.driftPct > 0 ? '▲' : '▼'} {Math.abs(d.driftPct).toFixed(1)}%
                                </span>
                                <p className="text-[9px] text-zinc-400">
                                  {formatCurrency(d.snapshotPrice)} → {formatCurrency(d.currentPrice)}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })()}
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => {
                        onUpdateQuoteStatus(selectedQuote.id, 'approved');
                        setSelectedQuote({ 
                          ...selectedQuote, 
                          status: 'approved',
                          statusHistory: [
                            ...(selectedQuote.statusHistory || []),
                            { status: 'approved', timestamp: new Date().toISOString() }
                          ]
                        });
                        showToast('Venda Fechada!', 'success');
                      }}
                      className="bg-emerald-600 text-white py-3.5 rounded-xl font-bold uppercase tracking-wider text-sm shadow-md hover:bg-emerald-700 transition-colors flex items-center justify-center gap-2"
                    >
                      <CheckCircle2 className="h-4 w-4" />
                      Fechar Venda
                    </button>
                    <button
                      onClick={() => {
                        onUpdateQuoteStatus(selectedQuote.id, 'rejected');
                        setSelectedQuote({ 
                          ...selectedQuote, 
                          status: 'rejected',
                          statusHistory: [
                            ...(selectedQuote.statusHistory || []),
                            { status: 'rejected', timestamp: new Date().toISOString() }
                          ]
                        });
                        showToast('Marcado como perdido.', 'info');
                      }}
                      className="bg-red-50 text-red-600 py-3.5 rounded-xl font-bold uppercase tracking-wider text-sm border border-red-100 hover:bg-red-100 transition-colors flex items-center justify-center gap-2"
                    >
                      <XCircle className="h-4 w-4" />
                      Recusado
                    </button>
                  </div>
                  
                  <button 
                    onClick={() => handleFollowUp(selectedQuote)}
                    className="w-full bg-[#25D366] hover:bg-[#128C7E] text-white py-3.5 rounded-xl flex items-center justify-center gap-2 font-bold uppercase tracking-widest text-sm transition-transform active:scale-[0.98] shadow-lg"
                  >
                    <Send className="h-4 w-4" /> Cobrar Retorno do Orçamento
                  </button>
                </div>
              )}

              <div className="pt-4 border-t border-zinc-100">
                <React.Suspense fallback={<div className="h-20 w-full animate-pulse bg-zinc-100 rounded-xl" />}>
                  <PDFExporter
                    user={user as any}
                    formData={{
                      ...selectedQuote,
                      clientName: selectedQuote.clientName,
                      serviceName: selectedQuote.rawServiceName,
                      materialCost: selectedQuote.materialCost,
                      timeHours: Math.floor(selectedQuote.timeSpentMinutes / 60),
                      timeMinutes: selectedQuote.timeSpentMinutes % 60,
                      products: selectedQuote.products,
                      services: selectedQuote.services
                    }}
                    finalPrice={selectedQuote.finalPrice}
                    quoteCount={quotes.length}
                    templateType={CATEGORIES.find(c => c.name === selectedQuote.category)?.templateType as any || 'STANDARD'}
                    saved={true}
                  />
                </React.Suspense>
              </div>

              {/* Ação Genérica: Todos os status podem ser reutilizados */}
              <div className="pt-4 mt-4 border-t border-zinc-100">
                <button
                  onClick={() => {
                    onReuseQuote(selectedQuote);
                    setSelectedQuote(null);
                  }}
                  className="w-full bg-zinc-100 text-zinc-700 py-3.5 rounded-xl font-bold uppercase tracking-wider text-sm border border-zinc-200 hover:bg-zinc-200 transition-colors flex items-center justify-center gap-2"
                >
                  <RefreshCcw className="h-4 w-4" />
                  REUTILIZAR ORÇAMENTO
                </button>
              </div>

            </div>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      <AnimatePresence>
        {toastMessage && (
          <motion.div
            initial={{ opacity: 0, y: 50, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.9 }}
            className="fixed bottom-6 left-1/2 -translate-x-1/2 z-100 px-4 w-full max-w-sm"
          >
            <div className={`flex items-center gap-3 px-6 py-4 rounded-2xl shadow-xl border ${
              toastMessage.type === 'success' ? 'bg-brand border-brand/50 text-white' :
              toastMessage.type === 'error' ? 'bg-rose-600 border-rose-500 text-white' :
              'bg-zinc-900 border-zinc-800 text-white'
            }`}>
              {toastMessage.type === 'success' && <CheckCircle2 className="h-5 w-5 shrink-0" />}
              {toastMessage.type === 'error' && <XCircle className="h-5 w-5 shrink-0" />}
              {toastMessage.type === 'info' && <Clock3 className="h-5 w-5 shrink-0" />}
              <p className="font-bold text-sm">{toastMessage.title}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
